// 예약 작업 1건 - 데스크탑 표용 Row, 모바일 카드용 Card 둘 다 여기서 만든다(둘 다 같은 props).
// 공통 조각(상태 칩/다음 실행/버튼)을 한 군데서 만들어 표/카드가 서로 다르게 보이는 일이 없게 한다.
import React from 'react';
import {
  TableRow, TableCell, Card, CardContent, Box, Typography, Chip, Button, Stack, Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  statusMeta, jobSrcLabel, formatDuration, formatDateTimeShort, formatNextRun, canRunManually,
} from '../../lib/sysJobUtils';

const StatusChip = ({ job }) => {
  const meta = statusMeta(job.lastStatus);
  return <Chip size="small" label={meta.label} color={meta.color} variant={job.lastStatus ? 'filled' : 'outlined'} />;
};

const RunButton = ({ job, onRun, running }) => {
  if (!canRunManually(job)) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<PlayArrowIcon fontSize="small" />}
      disabled={running || job.lastStatus === 'RUNNING'}
      onClick={(e) => { e.stopPropagation(); onRun(job); }}
    >
      지금 실행
    </Button>
  );
};

const OverdueBadge = ({ overdue }) => {
  if (!overdue) return null;
  return (
    <Tooltip title="예상 주기보다 오래 실행되지 않았습니다">
      <WarningAmberIcon fontSize="small" color="warning" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
    </Tooltip>
  );
};

// ── 데스크탑: 표 행 ──────────────────────────────────────────────────────
export const SysJobTableRow = ({ job, onClick, onRun, running, expanded }) => (
  <TableRow
    hover
    onClick={() => onClick(job)}
    sx={{ cursor: 'pointer', bgcolor: expanded ? 'action.selected' : undefined }}
  >
    <TableCell>
      <Typography variant="body2" fontWeight={600}>{job.jobNm}</Typography>
      <Typography variant="caption" color="text.secondary">{job.jobId}</Typography>
    </TableCell>
    <TableCell>{jobSrcLabel(job.jobSrc)}</TableCell>
    <TableCell>{job.scheduleDesc || '-'}</TableCell>
    <TableCell>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <StatusChip job={job} />
        <Typography variant="caption" color="text.secondary">
          {formatDateTimeShort(job.lastStartDt)}
          {job.lastDurationMs != null ? ` (${formatDuration(job.lastDurationMs)})` : ''}
        </Typography>
      </Stack>
    </TableCell>
    <TableCell>{formatDateTimeShort(job.lastSuccessDt)}</TableCell>
    <TableCell>
      {formatNextRun(job.nextRunDt, job.overdue)}
      <OverdueBadge overdue={job.overdue} />
    </TableCell>
    <TableCell align="right">
      <RunButton job={job} onRun={onRun} running={running} />
    </TableCell>
  </TableRow>
);

// ── 모바일: 카드 ────────────────────────────────────────────────────────
export const SysJobCard = ({ job, onClick, onRun, running, expanded }) => (
  <Card
    variant="outlined"
    onClick={() => onClick(job)}
    sx={{ mb: 1.5, cursor: 'pointer', borderColor: expanded ? 'primary.main' : undefined }}
  >
    <CardContent sx={{ py: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>{job.jobNm}</Typography>
          <Typography variant="caption" color="text.secondary">
            {jobSrcLabel(job.jobSrc)} · {job.scheduleDesc || '-'}
          </Typography>
        </Box>
        <StatusChip job={job} />
      </Stack>

      <Stack spacing={0.25} sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          마지막 실행: {formatDateTimeShort(job.lastStartDt)}
          {job.lastDurationMs != null ? ` (${formatDuration(job.lastDurationMs)})` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          마지막 성공: {formatDateTimeShort(job.lastSuccessDt)}
        </Typography>
        <Typography variant="caption" color={job.overdue ? 'warning.main' : 'text.secondary'}>
          다음 실행: {formatNextRun(job.nextRunDt, job.overdue)}
          <OverdueBadge overdue={job.overdue} />
        </Typography>
      </Stack>

      {canRunManually(job) && (
        <Box sx={{ mt: 1 }}>
          <RunButton job={job} onRun={onRun} running={running} />
        </Box>
      )}
    </CardContent>
  </Card>
);
